import { useEffect, useRef } from 'react'
import { Loading } from './Loading'

interface InfiniteScrollSentinelProps {
	/** Callback to trigger when the sentinel becomes visible */
	onLoadMore: () => void
	/** Whether there are more items to load */
	hasMore: boolean
	/** Whether currently loading more items */
	isLoading: boolean
	/** Root margin for IntersectionObserver (triggers before sentinel is visible) */
	rootMargin?: string
	/** Optional class name for styling */
	className?: string
}

/**
 * InfiniteScrollSentinel - A component that triggers loading more items when scrolled into view
 *
 * Place this component at the bottom of your scrollable list. When it becomes visible
 * (or near visible based on rootMargin), it will call onLoadMore to fetch the next page.
 *
 * @example
 * <div className="content-list">
 *   {items.map(item => <ItemCard key={item.id} {...item} />)}
 *   <InfiniteScrollSentinel
 *     onLoadMore={fetchNextPage}
 *     hasMore={hasNextPage}
 *     isLoading={isFetchingNextPage}
 *   />
 * </div>
 */
export function InfiniteScrollSentinel({
										   onLoadMore,
										   hasMore,
										   isLoading,
										   rootMargin = '200px',
										   className = '',
									   }: InfiniteScrollSentinelProps) {
	const sentinelRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		// Don't observe if we can't load more or we're already loading
		if (!hasMore || isLoading) return

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry?.isIntersecting) {
					onLoadMore()
				}
			},
			{ rootMargin },
		)

		const sentinel = sentinelRef.current
		if (sentinel) {
			observer.observe(sentinel)
		}

		return () => {
			if (sentinel) {
				observer.unobserve(sentinel)
			}
		}
	}, [hasMore, isLoading, onLoadMore, rootMargin])

	return (
		<div
			ref={sentinelRef}
			className={`infinite-scroll-sentinel ${className}`}
			style={{ height: '1px', margin: '1rem 0' }}
		>
			{isLoading && (
				<div style={{ padding: '1rem' }}>
					<Loading text="Loading more..." size="sm" />
				</div>
			)}
		</div>
	)
}
